import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAppUser } from "../../../redux/appAuthSlice";
import { isHouseOperator } from "../../../utils/houseAccess";
import { ArrowUpRight, CircleDot, Flame, MessageCircle, Plus, Sparkles } from "lucide-react";
import { getCreateBetHouseNav } from "../../../utils/createBetHouseHelpers";
import {
  computeOddsPercents,
  fmtCompact,
  fmtOdds,
  getFeaturedFixture,
  getFixtureMarketCount,
  getHouseBettorsTotal,
  getHousePoolTotal,
  isFixtureLive,
  isFixtureSoon,
  isFreshHouse,
} from "../../../utils/bondhouseHelpers";
import { getFixtureScore } from "../../../utils/betDetailHelpers";
import { PLACE_BET_FIXTURE_PATH, PLACE_BET_PATH } from "../../../utils/bettingDeskHelpers";
import { formatAppTime, prefers12HourClock } from "../../../utils/appTimeFormat";
import ShareIconButton from "../../ShareIconButton";
import {
  buildHouseShareLink,
  buildHouseShareText,
} from "../../../utils/houseShareHelpers";
import "../../../share-icon.css";

const getInitials = (name = "") => {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getKickParts = (iso) => {
  if (!iso) return { date: null, time: "—" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: null, time: "—" };

  const use12Hour = prefers12HourClock();
  const time = formatAppTime(d, { includeSeconds: false, use12Hour });
  const sameDay = d.toDateString() === new Date().toDateString();

  if (sameDay) return { date: null, time };

  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return { date, time };
};

const fmtKickTape = (iso) => {
  const { date, time } = getKickParts(iso);
  if (!date) return time;
  return `${date} · ${time}`;
};

const KickCell = ({ iso }) => {
  const { date, time } = getKickParts(iso);
  return (
    <span className="floor-board-col floor-board-col-kick">
      {date ? <span className="floor-board-kick-date">{date}</span> : null}
      <span className="floor-board-kick-time">{time}</span>
    </span>
  );
};

const Crest = ({ team }) => (
  <span className="floor-crest">
    {team?.logo ? (
      <img src={team.logo} alt="" />
    ) : (
      <span>{(team?.name || "?").slice(0, 1)}</span>
    )}
  </span>
);

const OddsCell = ({ label, value, houseId, fixtureId, accent }) => (
  <Link
    to={PLACE_BET_FIXTURE_PATH(houseId, fixtureId)}
    className={`floor-odds-cell floor-odds-cell-${accent || "mid"}`}
    title={`Bet ${label} @ ${fmtOdds(value)}`}
  >
    <span className="floor-odds-label">{label}</span>
    <span className="floor-odds-val">{fmtOdds(value)}</span>
  </Link>
);

const BoardRow = ({ houseId, fixture, idx, featured }) => {
  const live = isFixtureLive(fixture);
  const soon = !live && isFixtureSoon(fixture);
  const o = fixture?.odds?.oneXTwo?.fullTime || fixture?.odds?.oneXTwo?.firstHalf;
  const home = fixture?.teams?.home;
  const away = fixture?.teams?.away;
  const percents = computeOddsPercents(fixture?.odds);
  const marketCount = getFixtureMarketCount(fixture);

  return (
    <div
      className={`floor-board-row ${featured ? "floor-board-row-featured" : ""} ${
        live ? "floor-board-row-live" : ""
      }`}
    >
      <span className="floor-board-col floor-board-col-idx">
        {String(idx + 1).padStart(2, "0")}
      </span>
      <span className="floor-board-col floor-board-col-match">
        <span className="floor-board-teams">
          <Crest team={home} />
          <span className="floor-board-team-name">{home?.name || "TBD"}</span>
          <span className="floor-board-vs">vs</span>
          <span className="floor-board-team-name">{away?.name || "TBD"}</span>
          <Crest team={away} />
        </span>
        <span className="floor-board-meta">
          {fixture?.league?.name && (
            <span className="floor-board-league">{fixture.league.name}</span>
          )}
          {live && (
            <span className="floor-board-pill floor-board-pill-live">
              <CircleDot size={8} /> Live
            </span>
          )}
          {!live && soon && (
            <span className="floor-board-pill floor-board-pill-soon">Soon</span>
          )}
          <span className="floor-board-markets">
            {marketCount} mkts
          </span>
        </span>
        {percents && (
          <span className="floor-board-implied" aria-hidden>
            <span style={{ flex: percents.home || 1 }} />
            <span style={{ flex: percents.draw || 1 }} />
            <span style={{ flex: percents.away || 1 }} />
          </span>
        )}
      </span>
      <KickCell iso={fixture?.date} />
      <div className="floor-board-odds-line">
        {o ? (
          <>
            <span className="floor-board-col floor-board-col-odds-slot">
              <OddsCell
                label="1"
                value={o["1"]}
                houseId={houseId}
                fixtureId={fixture?._id}
                accent="home"
              />
            </span>
            <span className="floor-board-col floor-board-col-odds-slot">
              <OddsCell
                label="X"
                value={o.X}
                houseId={houseId}
                fixtureId={fixture?._id}
                accent="draw"
              />
            </span>
            <span className="floor-board-col floor-board-col-odds-slot">
              <OddsCell
                label="2"
                value={o["2"]}
                houseId={houseId}
                fixtureId={fixture?._id}
                accent="away"
              />
            </span>
          </>
        ) : (
          <span className="floor-board-col floor-board-col-odds-pending">
            <span className="floor-board-odds-wait">Pending</span>
          </span>
        )}
      </div>
      <Link
        to={PLACE_BET_FIXTURE_PATH(houseId, fixture?._id)}
        className="floor-board-col floor-board-col-go"
        aria-label="Open betting slip for this fixture"
      >
        <span className="floor-board-go-label">Bet Now</span>
        <ArrowUpRight size={12} />
      </Link>
    </div>
  );
};

const FloorTape = ({ house, fixture }) => {
  if (!fixture) return null;
  const live = isFixtureLive(fixture);
  const soon = !live && isFixtureSoon(fixture);
  const o = fixture?.odds?.oneXTwo?.fullTime || fixture?.odds?.oneXTwo?.firstHalf;
  const home = fixture?.teams?.home;
  const away = fixture?.teams?.away;
  const percents = computeOddsPercents(fixture?.odds);
  const { showScore, home: homeGoals, away: awayGoals, short } = getFixtureScore(fixture);

  return (
    <div className="floor-tape" aria-label="Featured line">
      <span className="floor-tape-label">Now on tape</span>
      <div className="floor-tape-body">
        <div className="floor-tape-match">
          <Crest team={home} />
          <span className="floor-tape-team">{home?.name || "TBD"}</span>
          {showScore && (homeGoals != null || awayGoals != null) ? (
            <span className="floor-tape-status floor-tape-status-score tabular-nums">
              {homeGoals ?? 0}–{awayGoals ?? 0}
            </span>
          ) : (
            <span className="floor-tape-status">{short}</span>
          )}
          <span className="floor-tape-team">{away?.name || "TBD"}</span>
          <Crest team={away} />
        </div>
        <div className="floor-tape-meta">
          {fixture?.league?.name && (
            <span>{fixture.league.name}</span>
          )}
          <span>{fmtKickTape(fixture?.date)}</span>
          <span className="floor-board-markets">
            {getFixtureMarketCount(fixture)} mkts
          </span>
          {live && (
            <span className="floor-tape-live">
              <CircleDot size={9} /> Live
            </span>
          )}
          {!live && soon && <span className="floor-tape-soon">Kickoff soon</span>}
        </div>
        {percents && (
          <div className="floor-tape-implied" aria-hidden>
            <span style={{ width: `${percents.home}%` }} />
            <span style={{ width: `${percents.draw}%` }} />
            <span style={{ width: `${percents.away}%` }} />
          </div>
        )}
        <div className="floor-tape-odds">
          {o ? (
            <>
              <OddsCell
                label="1"
                value={o["1"]}
                houseId={house._id}
                fixtureId={fixture._id}
                accent="home"
              />
              <OddsCell
                label="X"
                value={o.X}
                houseId={house._id}
                fixtureId={fixture._id}
                accent="draw"
              />
              <OddsCell
                label="2"
                value={o["2"]}
                houseId={house._id}
                fixtureId={fixture._id}
                accent="away"
              />
            </>
          ) : (
            <span className="floor-board-odds-wait">Odds pending</span>
          )}
        </div>
      </div>
    </div>
  );
};

const FloorDetail = ({ house, emptyFloor = false, isAuthenticated = false, onChatOpen }) => {
  const user = useSelector(selectAppUser);
  const canManage = useMemo(
    () => isHouseOperator(house, user?._id),
    [house, user?._id]
  );

  const stats = useMemo(() => {
    if (!house) return null;
    return {
      fixtures: house.fixtures?.length || 0,
      bettors: getHouseBettorsTotal(house),
      pool: getHousePoolTotal(house),
      followers: house?.followers?.length || house?.followersCount || 0,
    };
  }, [house]);

  const createNav = getCreateBetHouseNav(isAuthenticated);

  if (!house) {
    return (
      <section className="floor-detail floor-detail-idle">
        <div className="floor-idle">
          <p className="floor-idle-eyebrow">BondHouse · The floor</p>
          <p className="floor-idle-title">
            {emptyFloor ? "No houses on the floor yet" : "Select a listing from the ledger"}
          </p>
          <p className="floor-idle-copy">
            {emptyFloor
              ? "Be the first operator — name your house, allocate Cash, and list fixtures from the operator console."
              : "Each row is a bet house with live lines. Pick one to read the board and place a wager."}
          </p>
          <Link to={createNav.to} state={createNav.state} className="floor-idle-cta">
            <Plus size={12} strokeWidth={2.5} aria-hidden />
            Open a house
          </Link>
          {!emptyFloor ? (
            <p className="floor-idle-hint">Or launch your own desk with the button above.</p>
          ) : null}
        </div>
      </section>
    );
  }

  const fresh = isFreshHouse(house);
  const hot = (stats?.bettors || 0) > 0;
  const hasLive = (house?.fixtures || []).some(isFixtureLive);
  const featured = getFeaturedFixture(house);
  const fixtures = house?.fixtures || [];
  const primaryTo = canManage
    ? `/app/bethouse/${house._id}/manage`
    : PLACE_BET_PATH(house._id);

  return (
    <section className="floor-detail" aria-label={house?.name}>
      <header className="floor-desk">
        <div className="floor-desk-left">
          <span className="floor-desk-avatar">{getInitials(house?.name)}</span>
          <div className="floor-desk-copy">
            <p className="floor-desk-eyebrow">Bet house</p>
            <h1 className="floor-desk-name" title={house?.name}>
              {house?.name || "Unnamed"}
            </h1>
            {house?.description && (
              <p className="floor-desk-desc">{house.description}</p>
            )}
          </div>
        </div>

        <div className="floor-desk-toolbar-mobile">
          <Link
            to={primaryTo}
            className={`floor-desk-chip floor-desk-chip-bet${
              canManage ? " floor-desk-chip-manage" : ""
            }`}
          >
            {canManage ? "Manage" : "Bet Now"}
          </Link>
          <Link
            to={`/app/bethouse/${house._id}`}
            className="floor-desk-chip floor-desk-chip-view"
          >
            View
            <ArrowUpRight size={10} />
          </Link>
          <button
            type="button"
            className="floor-desk-chip floor-desk-chip-icon"
            onClick={() => onChatOpen?.(house)}
            aria-label="Chat"
          >
            <MessageCircle size={14} />
          </button>
          <ShareIconButton
            className="floor-desk-chip floor-desk-chip-icon floor-desk-share"
            size={14}
            url={buildHouseShareLink(house._id)}
            text={buildHouseShareText(house)}
            title={house?.name || "BetBond house"}
            ariaLabel="Share house"
          />
        </div>

        <div className="floor-desk-meta">
          <div className="floor-desk-stats">
            <span className="floor-desk-stat">
              <span className="floor-desk-stat-val">{stats?.fixtures}</span>
              <span className="floor-desk-stat-key">fx</span>
            </span>
            <span className="floor-desk-stat">
              <span className="floor-desk-stat-val">{stats?.bettors}</span>
              <span className="floor-desk-stat-key">bet</span>
            </span>
            <span className="floor-desk-stat">
              <span className="floor-desk-stat-val">{fmtCompact(stats?.pool)}</span>
              <span className="floor-desk-stat-key">pool</span>
            </span>
            <span className="floor-desk-stat">
              <span className="floor-desk-stat-val">{stats?.followers}</span>
              <span className="floor-desk-stat-key">fol</span>
            </span>
          </div>

          <div className="floor-desk-tags">
            {hasLive && (
              <span className="floor-desk-tag floor-desk-tag-live">
                <CircleDot size={8} /> Live
              </span>
            )}
            {hot && (
              <span className="floor-desk-tag floor-desk-tag-hot">
                <Flame size={8} /> Hot
              </span>
            )}
            {fresh && (
              <span className="floor-desk-tag floor-desk-tag-fresh">
                <Sparkles size={8} /> Fresh
              </span>
            )}
            <Link
              to={`/app/bethouse/${house._id}`}
              className="floor-desk-cta floor-desk-cta-desktop"
            >
              View house
              <ArrowUpRight size={11} />
            </Link>
          </div>
        </div>

        <div className="floor-desk-actions floor-desk-actions-desktop">
          <button
            type="button"
            className="floor-desk-icon"
            onClick={() => onChatOpen?.(house)}
            aria-label="Chat"
          >
            <MessageCircle size={13} />
          </button>
          <ShareIconButton
            className="floor-desk-icon floor-desk-share"
            size={13}
            url={buildHouseShareLink(house._id)}
            text={buildHouseShareText(house)}
            title={house?.name || "BetBond house"}
            ariaLabel="Share house"
          />
        </div>
      </header>

      <FloorTape house={house} fixture={featured} />

      <div className="floor-board">
        <div className="floor-board-header" role="row">
          <span className="floor-board-col floor-board-col-idx">#</span>
          <span className="floor-board-col floor-board-col-match">Match</span>
          <span className="floor-board-col floor-board-col-kick">Kick</span>
          <span className="floor-board-col floor-board-col-odds-h">1</span>
          <span className="floor-board-col floor-board-col-odds-h">X</span>
          <span className="floor-board-col floor-board-col-odds-h">2</span>
          <Link
            to={primaryTo}
            className={`floor-board-col floor-board-col-action floor-board-bet-now${
              canManage ? " floor-board-manage" : ""
            }`}
          >
            {canManage ? "Manage" : "Bet Now"}
          </Link>
        </div>
        <div className="floor-board-action-bar">
          <Link
            to={primaryTo}
            className={`floor-board-bet-now${
              canManage ? " floor-board-manage" : ""
            }`}
          >
            {canManage ? "Manage" : "Bet Now"}
          </Link>
        </div>
        <div className="floor-board-body">
          {fixtures.length > 0 ? (
            fixtures.map((fx, i) => (
              <BoardRow
                key={fx._id || i}
                houseId={house._id}
                fixture={fx}
                idx={i}
                featured={featured?._id === fx._id}
              />
            ))
          ) : (
            <p className="floor-board-empty">No fixtures on the board yet.</p>
          )}
        </div>
      </div>

    </section>
  );
};

export default FloorDetail;
